import { selector } from "../../../api";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { boundaryCRUD } from "../../../actions/boundary";
import { dictionaryCRUD } from "../../../actions/dictionary";

const boundarySchema = Yup.object().shape({
  boundary_code: Yup.string().required("Boundary code is required"),
  boundary_name: Yup.string().required("Boundary name is required"),
  boundary_initials: Yup.string().required("Boundary initials required"),
  postcode: Yup.string(),
  boundary_parent: Yup.string(),
  dictionary_boundary_type: Yup.string(),
  island_id: Yup.string().required("Island is required"),
  centroid_lat: Yup.number().nullable().min(-90, 'Latitude must be >= -90').max(90, 'Latitude must be <= 90'),
  centroid_lng: Yup.number().nullable().min(-180, 'Longitude must be >= -180').max(180, 'Longitude must be <= 180'),
  boundary_polygon: Yup.string().nullable(),
});

const initialValues = {
  boundary_code: "",
  boundary_name: "",
  boundary_initials: "",
  postcode: "",
  dictionary_boundary_type: 0,
  boundary_parent: 0,
  island_id: 0,
  centroid_lat: "",
  centroid_lng: "",
  centroid_coords: "", // single "lat, lng" input; derives centroid_lat/lng
  boundary_polygon: "",
  boundary_line: "",
};

// Mapping of boundary level names to their dictionary_boundary_type IDs
// Original four are stable ids. Ward/Street ids are environment-specific
// (auto-assigned), so they're resolved by NAME from the boundary-type dictionary.
const BOUNDARY_TYPE_IDS = {
  "Region": 21,
  "District": 22,
  "Locality": 23,
  "Shehia": 23,
  "Zone": 24,
};

// Levels whose parent is a District (Ward & Shehia are siblings under District).
const PARENT_IS_DISTRICT = ["Locality", "Shehia", "Ward"];
// Levels whose parent is a Shehia (Street & Zone are siblings under Shehia).
const PARENT_IS_SHEHIA = ["Zone", "Street"];

export const AddRegion = (props) => {
  const btnCLoseRef = useRef();
  const { setIndex, index, boundaryName } = props;
  const [loading, setLoading] = useState(false);
  const [islandList, setIslandList] = useState([]);
  const user = selector.User();

  // Cascading dropdown states
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [shehias, setShehias] = useState([]);
  const [selectedIsland, setSelectedIsland] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  // A Shehia user adding a Zone/Street: lock the parent to THEIR Shehia
  // (no Region/District cascade — their boundary_id is the Shehia).
  const shehiaUserLock =
    !user?.is_staff &&
    user?.admin_access === "Shehia" &&
    user?.boundary_id &&
    PARENT_IS_SHEHIA.includes(boundaryName);

  // Determine if cascading is needed (any level that has a parent above District).
  // Shehia-locked users skip the cascade entirely (parent is fixed).
  const needsCascading =
    !shehiaUserLock &&
    (PARENT_IS_DISTRICT.includes(boundaryName) || PARENT_IS_SHEHIA.includes(boundaryName));

  // Boundary-type id resolution: original four are static; Ward/Street resolved by name.
  const [boundaryTypes, setBoundaryTypes] = useState([]);
  const resolveTypeId = () => {
    if (BOUNDARY_TYPE_IDS[boundaryName]) return BOUNDARY_TYPE_IDS[boundaryName];
    const match = boundaryTypes.find(
      (t) => (t.dictionary_item_name_en || "").toLowerCase() === boundaryName.toLowerCase()
    );
    return match ? match.id : 0;
  };

  // Load islands + boundary-type dictionary (id 2) from backend
  useEffect(() => {
    boundaryCRUD
      .loadAllIslands()
      .then((resp) => {
        setIslandList(resp.data);
      })
      .catch((error) => {
        console.log(error);
      });
    dictionaryCRUD
      .loadDictionaryData(100, 0, "", 2)
      .then((resp) => setBoundaryTypes(resp.data.results))
      .catch(() => {});
  }, []);

  // Load regions when island is selected (filtered by island)
  useEffect(() => {
    // Reset dependent dropdowns when island changes
    setSelectedRegion(null);
    setRegions([]);
    setDistricts([]);
    setShehias([]);
    if (needsCascading && selectedIsland) {
      boundaryCRUD.loadBoundaryByIsland(1000, 0, 'Region', selectedIsland).then((resp) => {
        setRegions(resp.data.results);
      }).catch((error) => {
        console.log(error);
      });
    }
  }, [selectedIsland]);

  // Load districts when region is selected
  useEffect(() => {
    if (selectedRegion) {
      boundaryCRUD.loadBoundaryByParent(selectedRegion).then((resp) => {
        setDistricts(resp.data);
      }).catch((error) => {
        console.log(error);
      });
    }
    setSelectedDistrict(null);
    setShehias([]);
  }, [selectedRegion]);

  // Load shehias when district is selected (for Street/Zone whose parent is a Shehia)
  useEffect(() => {
    if (selectedDistrict && PARENT_IS_SHEHIA.includes(boundaryName)) {
      boundaryCRUD.loadBoundaryByParent(selectedDistrict).then((resp) => {
        setShehias(resp.data);
      }).catch((error) => {
        console.log(error);
      });
    }
  }, [selectedDistrict]);

  const formik = useFormik({
    initialValues,
    validationSchema: boundarySchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const {
        boundary_code,
        boundary_name,
        boundary_initials,
        postcode,
        boundary_parent,
        island_id,
        centroid_lat,
        centroid_lng,
        boundary_polygon,
        boundary_line,
      } = values;

      // Auto-set boundary type based on the level being created (name-resolved).
      const dictionary_boundary_type = resolveTypeId();

      const data = {
        boundary_code,
        boundary_name,
        boundary_initials,
        postcode: postcode || null,
        boundary_parent: boundary_parent || null,
        dictionary_boundary_type,
        island_id,
        centroid_lat: centroid_lat !== '' ? parseFloat(centroid_lat) : null,
        centroid_lng: centroid_lng !== '' ? parseFloat(centroid_lng) : null,
        boundary_polygon: boundary_polygon || null,
        boundary_line: boundary_line || null,
      };

      setLoading(true);
      setTimeout(() => {
        boundaryCRUD
          .addBoundary(data)
          .then((resp) => {
            toast.success(`${props.boundaryName} has been created.`, {
              autoClose: 7000,
            });
            resetForm();
            setSelectedIsland(null);
            setSelectedRegion(null);
            setSelectedDistrict(null);
            btnCLoseRef.current.click()
            setIndex(index + 1);
            setLoading(false);
            setSubmitting(false);
          })
          .catch((error) => {
            let errorMessage = 'Failed to create boundary';
            if (error.response?.data) {
              const errorData = error.response.data;
              if (errorData.boundary_code) {
                errorMessage = errorData.boundary_code[0];
              } else if (errorData.error) {
                errorMessage = errorData.error;
              } else if (typeof errorData === 'object') {
                const firstKey = Object.keys(errorData)[0];
                if (firstKey && Array.isArray(errorData[firstKey])) {
                  errorMessage = `${firstKey}: ${errorData[firstKey][0]}`;
                }
              }
            }
            toast.error(errorMessage, { autoClose: 7000 });
            setLoading(false);
            setSubmitting(false);
          });

      }, 2000);
    },
  });

  // Shehia user: fix the parent to their own Shehia.
  useEffect(() => {
    if (shehiaUserLock) {
      formik.setFieldValue("boundary_parent", user.boundary_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shehiaUserLock]);

  return (
    <>
      <div
        class="modal fade addRegion"
        tabindex="-1"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="myLargeModalLabel">
                New {props.boundaryName}
              </h5>
              <button
                ref={btnCLoseRef}
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <hr />
            <form onSubmit={formik.handleSubmit}>
              <div class="modal-body">
                <div className="row">
                  <div className="col-md-4 col-sm-12">
                    <div class="mb-3">
                      <label for="boundary_code" class="form-label">
                        Boundary Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="boundary_code"
                        {...formik.getFieldProps("boundary_code")}
                        class="form-control"
                        placeholder="Boundary Code"
                      />
                      {formik.touched.boundary_code &&
                        formik.errors.boundary_code && (
                          <div className="fw-light h6 mt-1" style={{ fontSize: "15px" }}>
                            <span role="alert" className="text-danger">
                              {formik.errors.boundary_code}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-12">
                    <div class="mb-3">
                      <label for="boundary_name" class="form-label">
                        Boundary Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="boundary_name"
                        {...formik.getFieldProps("boundary_name")}
                        class="form-control"
                        placeholder="Boundary Name"
                      />
                      {formik.touched.boundary_name &&
                        formik.errors.boundary_name && (
                          <div className="fw-light h6 mt-1" style={{ fontSize: "15px" }}>
                            <span role="alert" className="text-danger">
                              {formik.errors.boundary_name}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-12">
                    <div class="mb-3">
                      <label for="boundary_initials" class="form-label">
                        Boundary Initials <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="boundary_initials"
                        {...formik.getFieldProps("boundary_initials")}
                        class="form-control"
                        placeholder="Boundary Inititials"
                      />
                      {formik.touched.boundary_initials &&
                        formik.errors.boundary_initials && (
                          <div className="fw-light h6 mt-1" style={{ fontSize: "15px" }}>
                            <span role="alert" className="text-danger">
                              {formik.errors.boundary_initials}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 col-sm-12">
                    <div class="mb-3">
                      <label for="postcode" class="form-label">Postcode</label>
                      <input
                        type="text"
                        id="postcode"
                        {...formik.getFieldProps("postcode")}
                        class="form-control"
                        placeholder="Postcode"
                      />
                    </div>
                  </div>
                </div>

                {/* Cascading: Island > Region > District > Shehia > Zone (read-only) */}
                {needsCascading && (
                  <>
                    <div className="row">
                      <div className="col-md-3 col-sm-12">
                        <div class="mb-3">
                          <label class="form-label">
                            1. Island <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={islandList?.map((dt) => ({
                              value: dt.id,
                              label: dt.name,
                            }))}
                            value={selectedIsland ? islandList.filter(i => i.id === selectedIsland).map(i => ({ value: i.id, label: i.name }))[0] : null}
                            placeholder="Select island"
                            onChange={(e) => {
                              setSelectedIsland(e.value);
                              formik.setFieldValue("island_id", e.value);
                            }}
                            isSearchable={true}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 col-sm-12">
                        <div class="mb-3">
                          <label class="form-label">
                            2. Region <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={regions?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={selectedRegion ? regions.filter(r => r.id === selectedRegion).map(r => ({ value: r.id, label: r.boundary_name }))[0] : null}
                            placeholder="Select region"
                            onChange={(e) => {
                              setSelectedRegion(e.value);
                              formik.setFieldValue("boundary_parent", 0);
                            }}
                            isSearchable={true}
                            isDisabled={!selectedIsland}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 col-sm-12">
                        <div class="mb-3">
                          <label class="form-label">
                            3. District {PARENT_IS_DISTRICT.includes(boundaryName) ? <span className="text-muted">(Parent)</span> : ""}<span className="text-danger"> *</span>
                          </label>
                          <Select
                            options={districts?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={selectedDistrict ? districts.filter(d => d.id === selectedDistrict).map(d => ({ value: d.id, label: d.boundary_name }))[0] : null}
                            placeholder="Select district"
                            onChange={(e) => {
                              setSelectedDistrict(e.value);
                              if (PARENT_IS_DISTRICT.includes(boundaryName)) {
                                formik.setFieldValue("boundary_parent", e.value);
                              } else {
                                formik.setFieldValue("boundary_parent", 0);
                              }
                            }}
                            isSearchable={true}
                            isDisabled={!selectedRegion}
                          />
                        </div>
                      </div>
                      {PARENT_IS_SHEHIA.includes(boundaryName) && (
                        <div className="col-md-3 col-sm-12">
                          <div class="mb-3">
                            <label class="form-label">
                              4. Shehia <span className="text-muted">(Parent)</span><span className="text-danger"> *</span>
                            </label>
                            <Select
                              options={shehias
                                ?.filter((dt) => {
                                  // District children now include Wards AND Shehias;
                                  // Street's parent must be a Shehia only.
                                  const tn = (dt.dictionary_boundary_type?.dictionary_item_name_en || "").toLowerCase();
                                  return !tn || tn === "locality" || tn === "shehia";
                                })
                                .map((dt) => ({
                                  value: dt.id,
                                  label: dt.boundary_name,
                                }))}
                              placeholder="Select shehia"
                              onChange={(e) => {
                                formik.setFieldValue("boundary_parent", e.value);
                              }}
                              isSearchable={true}
                              isDisabled={!selectedDistrict}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-md-4 col-sm-12">
                        <div class="mb-3">
                          <label class="form-label">Boundary Type</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={boundaryName}
                            disabled
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Non-cascading: Island + Parent + Type for Region/District */}
                {shehiaUserLock && (
                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div class="mb-3">
                        <label class="form-label">Shehia (Parent)</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={user.boundary_name || "My Shehia"}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!needsCascading && !shehiaUserLock && (
                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div class="mb-3">
                        <label class="form-label">
                          Island <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={islandList?.map((dt) => ({
                            value: dt.id,
                            label: dt.name,
                          }))}
                          placeholder="Select island"
                          onChange={(e) => {
                            formik.setFieldValue("island_id", e.value);
                          }}
                          isSearchable={true}
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-sm-12">
                      <div class="mb-3">
                        <label for="boundary_parent" class="form-label">
                          Boundary Parent <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={props.parentList?.map((dt) => ({
                            value: dt.id,
                            label: dt.boundary_name,
                          }))}
                          placeholder="Select parent"
                          onChange={(e) => {
                            formik.setFieldValue("boundary_parent", e.value);
                          }}
                          isSearchable={true}
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-sm-12">
                      <div class="mb-3">
                        <label class="form-label">Boundary Type</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={boundaryName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-12">
                    <h6 className="text-muted mb-3">Boundary Centroid</h6>
                  </div>
                  <div className="col-md-12 col-sm-12">
                    <div class="mb-3">
                      <label for="centroid_coords" class="form-label">
                        Centroid (Latitude, Longitude)
                      </label>
                      <input
                        type="text"
                        id="centroid_coords"
                        value={formik.values.centroid_coords}
                        onChange={(e) => {
                          const raw = e.target.value;
                          formik.setFieldValue("centroid_coords", raw);
                          // Parse "lat, lng" (comma or space separated) into the
                          // two backend fields, so lat/lng can't be swapped.
                          const parts = raw.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
                          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                            formik.setFieldValue("centroid_lat", parts[0]);
                            formik.setFieldValue("centroid_lng", parts[1]);
                          } else {
                            formik.setFieldValue("centroid_lat", "");
                            formik.setFieldValue("centroid_lng", "");
                          }
                        }}
                        class="form-control"
                        placeholder="e.g. -6.1659, 39.2026"
                      />
                      <small className="text-muted">
                        Paste as <strong>Latitude, Longitude</strong> (e.g. -6.1659, 39.2026) — same order as Google Maps.
                      </small>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    {(props.boundaryName === "Street" || props.boundaryName === "Road") ? (
                    <div class="mb-3">
                      <label class="form-label">Street Line</label>
                      <textarea
                        id="boundary_line"
                        {...formik.getFieldProps("boundary_line")}
                        className="form-control"
                        rows="4"
                        placeholder="Paste GeoJSON or WKT line data here..."
                      />
                      <small className="text-muted">Paste (Multi)LineString geometry in GeoJSON or WKT format</small>
                    </div>
                    ) : (
                    <div class="mb-3">
                      <label class="form-label">Boundary Polygon</label>
                      <textarea
                        id="boundary_polygon"
                        {...formik.getFieldProps("boundary_polygon")}
                        className="form-control"
                        rows="4"
                        placeholder="Paste GeoJSON or WKT polygon data here..."
                      />
                      <small className="text-muted">Paste MultiPolygon geometry in GeoJSON or WKT format</small>
                    </div>
                    )}
                  </div>
                </div>
              </div>
              <hr />
              <div className="modal-footer">
                <a
                  href="javascript:void(0);"
                  className="btn btn-soft-danger fw-medium"
                  data-bs-dismiss="modal"
                >
                  <i className="ri-close-line me-1 align-middle"></i> Close
                </a>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={
                    formik.isSubmitting ||
                    !formik.isValid ||
                    formik.values.island_id == 0
                  }
                >
                  {!loading && (
                    <span className="indicator-label">
                      <i className="las la-save la-lg"></i>save{" "}
                    </span>
                  )}
                  {loading && (
                    <span
                      className="indicator-progress"
                      style={{ display: "block" }}
                    >
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
